/*
 * Preview — VERQO lowercase, fitting the existing TRAQO visual DNA.
 *
 * Visit /preview/verqo-lowercase
 *
 * Constraints (from user):
 *   • lowercase wordmark only
 *   • match the current portal style: dark foreground + acid-lime accent
 *   • simple, minimal, clean, modern, up-to-date
 *
 * Reference: PortalShell.jsx sidebar uses
 *   <div className="w-8 h-8 rounded-md bg-foreground"> <span className="text-accent">T</span></div>
 *   <div className="display font-semibold leading-tight">TRAQO</div>
 *
 * Every lowercase variant below is shown inside that same shell structure
 * so the visual continuity is exact.
 */
import { useState } from "react";

// Tagline shortlist — same set as the previous exploration, but rendered
// inline at each variant card so the user can A/B them with the logo.
const TAGLINE_DEFAULT = "wholesale, verified.";
const TAGLINES = [
  "wholesale, verified.",
  "the trust layer for b2b trade.",
  "trade on rails.",
  "b2b commerce, end to end.",
  "wholesale you can verify.",
  "wholesale, instant.",
  "verified buyers. trusted suppliers.",
  "run your wholesale.",
];

export default function VerqoLowercasePreview() {
  const [tagline, setTagline] = useState(TAGLINE_DEFAULT);
  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Header */}
      <div className="border-b border-border bg-background">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/45">Brand · lowercase · current theme</div>
          <div className="display text-2xl">verqo — lowercase options</div>
          <div className="text-[12.5px] text-foreground/60 mt-1 max-w-2xl">Eight lowercase directions in the existing portal style — dark `foreground` + acid `accent`. Each shown in the actual sidebar context, on light, and at favicon scale. No palette switcher; we're staying inside the current visual system.</div>

          {/* Live tagline toggle */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] mono uppercase tracking-widest text-foreground/45">Tagline:</span>
            {TAGLINES.map(t => (
              <button
                key={t}
                onClick={() => setTagline(t)}
                className={`text-[11.5px] rounded-full px-3 py-1 border transition-colors ${tagline === t ? "bg-foreground text-background border-foreground" : "bg-background text-foreground/70 border-border hover:border-foreground/40"}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Current TRAQO reference row */}
      <div className="max-w-6xl mx-auto px-6 mt-6 mb-2">
        <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/45 mb-2">Current TRAQO — reference</div>
        <ReferenceRow/>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-4">
        {VARIANTS.map((v) => (
          <VariantRow key={v.id} v={v} tagline={tagline}/>
        ))}

        {/* Final picker section */}
        <div className="rounded-2xl border border-border bg-background p-6 mt-8">
          <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/45 mb-2">My pick if you want a recommendation</div>
          <div className="display text-2xl mb-2">Option 3 · "verqo" with accent dot after</div>
          <p className="text-[13px] text-foreground/65 leading-relaxed max-w-2xl">
            Cleanest read at every scale. The accent dot replaces the period — gives the wordmark a small visual hook without adding a separate mark. Pairs natively with the TRAQO accent-lime so no color rework needed. Works as both a full lockup and a standalone wordmark. Reads modern (Vercel · Linear · Notion follow this pattern). The "q" naturally carries the brand because lowercase-q is rare in tech brands — built-in distinctiveness.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Variant definitions — each is a render function that produces the logo
   in two flavors: full lockup (mark + wordmark) and wordmark-only.
   We keep the rendering in plain JSX/CSS (not SVG) so it reuses the app's
   exact foreground / accent / font tokens — guarantees pixel-match with
   the live portal.
   ───────────────────────────────────────────────────────────────────────── */
const VARIANTS = [
  {
    id: "v1",
    name: "1 · Mark + plain lowercase",
    desc: "Drop-in replacement for the current TRAQO mark. Black square, lime 'v', wordmark in display font, same sizes.",
    Mark: ({ size = 32 }) => (
      <div className="rounded-md bg-foreground flex items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-accent font-bold display" style={{ fontSize: size * 0.5 }}>v</span>
      </div>
    ),
    Word: ({ size = 18 }) => (
      <span className="display font-semibold leading-tight tracking-tight" style={{ fontSize: size }}>verqo</span>
    ),
  },
  {
    id: "v2",
    name: "2 · Mark + lowercase with subtle accent dot above 'q'",
    desc: "Same mark, but the wordmark has a tiny acid-accent dot floating above the 'q' — turns the bowl of the q into a hidden Q. Distinctive at a glance.",
    Mark: ({ size = 32 }) => (
      <div className="rounded-md bg-foreground flex items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-accent font-bold display" style={{ fontSize: size * 0.5 }}>v</span>
      </div>
    ),
    Word: ({ size = 18 }) => (
      <span className="display font-semibold leading-tight tracking-tight relative" style={{ fontSize: size }}>
        ver
        <span className="relative inline-block">q
          <span className="absolute" style={{
            top: -size * 0.22, left: "50%", transform: "translateX(-50%)",
            width: size * 0.18, height: size * 0.18, borderRadius: "50%",
            background: "var(--accent, #dcff3a)",
          }}/>
        </span>
        o
      </span>
    ),
  },
  {
    id: "v3",
    name: "3 · Wordmark-only · accent dot after",
    desc: "No square mark — just `verqo` followed by a single lime dot in place of a period. Vercel / Linear pattern. Most modern, most minimal.",
    Mark: null,
    Word: ({ size = 18 }) => (
      <span className="display font-semibold leading-tight tracking-tight inline-flex items-end" style={{ fontSize: size, gap: size * 0.08 }}>
        verqo<span style={{
          display: "inline-block",
          width: size * 0.18, height: size * 0.18, borderRadius: "50%",
          background: "var(--accent, #dcff3a)",
          marginBottom: size * 0.08,
        }}/>
      </span>
    ),
    standalone: true,
  },
  {
    id: "v4",
    name: "4 · Wordmark with the 'q' in accent",
    desc: "Pure type. The single 'q' letter is rendered in accent-lime — embedded brand hook, no extra glyph. Looks like an editor cursor highlight.",
    Mark: null,
    Word: ({ size = 18 }) => (
      <span className="display font-semibold leading-tight tracking-tight" style={{ fontSize: size }}>
        ver<span className="text-accent">q</span>o
      </span>
    ),
    standalone: true,
  },
  {
    id: "v5",
    name: "5 · Mark + lowercase + period",
    desc: "Adds a punctuation period like Notion or Plaid. Reads sharper, slightly more confident; works well in monospace contexts.",
    Mark: ({ size = 32 }) => (
      <div className="rounded-md bg-foreground flex items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-accent font-bold display" style={{ fontSize: size * 0.5 }}>v</span>
      </div>
    ),
    Word: ({ size = 18 }) => (
      <span className="display font-semibold leading-tight tracking-tight" style={{ fontSize: size }}>verqo<span className="text-accent">.</span></span>
    ),
  },
  {
    id: "v6",
    name: "6 · 'vq' monogram + wordmark",
    desc: "Use the 'vq' pairing as the square mark instead of just 'v'. Carries more of the brand. Pairs with the standard lowercase wordmark.",
    Mark: ({ size = 32 }) => (
      <div className="rounded-md bg-foreground flex items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-accent font-bold display tracking-tighter" style={{ fontSize: size * 0.45, letterSpacing: -size * 0.02 }}>vq</span>
      </div>
    ),
    Word: ({ size = 18 }) => (
      <span className="display font-semibold leading-tight tracking-tight" style={{ fontSize: size }}>verqo</span>
    ),
  },
  {
    id: "v7",
    name: "7 · Accent-fill square + dark 'v'",
    desc: "Inverts the mark — acid square, dark letter. Used by some fintechs (Robinhood-style). Pops against dark backgrounds; quieter on light.",
    Mark: ({ size = 32 }) => (
      <div className="rounded-md bg-accent flex items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-foreground font-bold display" style={{ fontSize: size * 0.5 }}>v</span>
      </div>
    ),
    Word: ({ size = 18 }) => (
      <span className="display font-semibold leading-tight tracking-tight" style={{ fontSize: size }}>verqo</span>
    ),
  },
  {
    id: "v8",
    name: "8 · Rounded-circle mark + lowercase",
    desc: "Same dark + lime energy but a perfect circle instead of a rounded square — more 'consumer fintech' (Cash App, Revolut). Slightly softer.",
    Mark: ({ size = 32 }) => (
      <div className="rounded-full bg-foreground flex items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-accent font-bold display" style={{ fontSize: size * 0.5 }}>v</span>
      </div>
    ),
    Word: ({ size = 18 }) => (
      <span className="display font-semibold leading-tight tracking-tight" style={{ fontSize: size }}>verqo</span>
    ),
  },
];

/* ── Each variant rendered across 4 contexts ──────────────────────────── */
function VariantRow({ v, tagline }) {
  const { Mark, Word } = v;
  return (
    <div className="rounded-2xl border border-border bg-background overflow-hidden" data-testid={`verqo-variant-${v.id}`}>
      <div className="px-5 py-3 border-b border-border flex items-baseline gap-3 flex-wrap">
        <div className="text-[13.5px] font-medium">{v.name}</div>
        <div className="text-[11.5px] text-foreground/55 leading-snug max-w-3xl">{v.desc}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
        {/* (a) Sidebar context — dark inverted ink */}
        <div className="px-5 py-6" style={{ background: "var(--foreground, #0c1612)", color: "var(--background, #f4f7f1)" }}>
          <div className="text-[9.5px] mono uppercase tracking-widest opacity-60 mb-3">Sidebar (dark)</div>
          <div className="flex items-center gap-2.5">
            {Mark && <Mark size={32}/>}
            <div>
              <div style={{ color: "var(--background, #f4f7f1)" }}><Word size={18}/></div>
              <div className="text-[10px] uppercase tracking-widest mt-0.5 opacity-50">Wholesale portal</div>
            </div>
          </div>
        </div>

        {/* (b) Light bg context — invoice / legal */}
        <div className="px-5 py-6 bg-background">
          <div className="text-[9.5px] mono uppercase tracking-widest text-foreground/45 mb-3">On light (invoice)</div>
          <div className="flex items-center gap-2.5">
            {Mark && <Mark size={32}/>}
            <div>
              <Word size={18}/>
              <div className="text-[10px] uppercase tracking-widest text-foreground/50 mt-0.5">{tagline}</div>
            </div>
          </div>
        </div>

        {/* (c) Hero size on light */}
        <div className="px-5 py-6 bg-secondary/30">
          <div className="text-[9.5px] mono uppercase tracking-widest text-foreground/45 mb-3">Hero scale</div>
          <div className="flex items-center gap-3">
            {Mark && <Mark size={56}/>}
            <Word size={42}/>
          </div>
        </div>

        {/* (d) Favicon / tight space */}
        <div className="px-5 py-6 bg-background flex items-center gap-4 flex-wrap">
          <div>
            <div className="text-[9.5px] mono uppercase tracking-widest text-foreground/45 mb-3">Favicon</div>
            <div className="flex items-center gap-2">
              {Mark ? <Mark size={16}/> : <span className="display font-bold text-[14px]">v.</span>}
              {Mark ? <Mark size={32}/> : <span className="display font-bold text-[24px]">v.</span>}
              {Mark ? <Mark size={48}/> : <span className="display font-bold text-[32px]">v.</span>}
            </div>
          </div>
          {v.standalone && (
            <div className="text-[10.5px] text-foreground/45 max-w-[140px] leading-snug">
              No square mark — wordmark-only. Standalone option.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Reference: how TRAQO currently looks (no changes — just for comparison). */
function ReferenceRow() {
  return (
    <div className="rounded-2xl border border-border bg-background overflow-hidden opacity-90">
      <div className="grid grid-cols-1 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
        <div className="px-5 py-6" style={{ background: "var(--foreground, #0c1612)", color: "var(--background, #f4f7f1)" }}>
          <div className="text-[9.5px] mono uppercase tracking-widest opacity-60 mb-3">Current · sidebar</div>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--background, #f4f7f1)" }}>
              <span className="font-bold display text-sm" style={{ color: "var(--foreground)" }}>T</span>
            </div>
            <div>
              <div className="display font-semibold leading-tight tracking-tight text-[18px]">TRAQO</div>
              <div className="text-[10px] uppercase tracking-widest opacity-50">Wholesale portal</div>
            </div>
          </div>
        </div>
        <div className="px-5 py-6 bg-background">
          <div className="text-[9.5px] mono uppercase tracking-widest text-foreground/45 mb-3">Current · on light</div>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-foreground flex items-center justify-center">
              <span className="text-accent font-bold display text-sm">T</span>
            </div>
            <div>
              <div className="display font-semibold leading-tight tracking-tight text-[18px]">TRAQO</div>
              <div className="text-[10px] uppercase tracking-widest text-foreground/50">B2B operating system</div>
            </div>
          </div>
        </div>
        <div className="px-5 py-6 bg-secondary/30">
          <div className="text-[9.5px] mono uppercase tracking-widest text-foreground/45 mb-3">Current · hero</div>
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-foreground flex items-center justify-center" style={{ width: 56, height: 56 }}>
              <span className="text-accent font-bold display" style={{ fontSize: 28 }}>T</span>
            </div>
            <span className="display font-semibold tracking-tight" style={{ fontSize: 42 }}>TRAQO</span>
          </div>
        </div>
        <div className="px-5 py-6 bg-background">
          <div className="text-[9.5px] mono uppercase tracking-widest text-foreground/45 mb-3">Current · favicons</div>
          <div className="flex items-center gap-2">
            {[16, 32, 48].map(s => (
              <div key={s} className="rounded-md bg-foreground flex items-center justify-center" style={{ width: s, height: s }}>
                <span className="text-accent font-bold display" style={{ fontSize: s * 0.5 }}>T</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
